import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  Pressable,
  Platform,
  View as RNView,
  Dimensions,
} from 'react-native';
import { TabView, SceneMap, TabBar } from 'react-native-tab-view';
import FindChargerMap from './FindChargerMap';
import SearchCharger from './SearchCharger';
import QRScanner from './QRScanner';
import * as Location from 'expo-location';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllChargers,
  getMyDistanceFromCharger,
} from '../store/actions/charging-actions';
import type { RootStackParamList } from '../navigation/stackTypes';
import { AppDispatch, RootState } from '../store';
import { getUserLocation } from '../components/common/utils/get-user-location';
import { ChargerAttributes } from '../models/charger-model';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getDeviceDimensions } from '../components/common/utils/platform.util';
import WithLoading from '../components/LoadingHOC';
import { navigationActions } from '../store/slices/navigation-slice';
// import TestLoadingComponent from '../components/UnitTestComponents/TestLoadingComponent';
// import TestSearchChargerCard from '../components/UnitTestComponents/TestSearchChargerCard';
// import TestSearchChargerWithLoader from '../components/UnitTestComponents/TestSearchChargerWithLoader';
// import TestRealData from '../components/UnitTestComponents/TestRealData';

// Map with Loader
const ChargerMapWithLoader = WithLoading(FindChargerMap);

// Search with Loader
const SearchChargerWithLoader = WithLoading(SearchCharger);

// For getting device dimensions
const { deviceWidth, deviceHeight } = getDeviceDimensions();

type Props = NativeStackScreenProps<RootStackParamList, 'ChargerSelection'>;

const TABS = [
  { key: 'Map', title: 'Map' },
  { key: 'Search', title: 'Search' },
  { key: 'ScanQr', title: 'Scan QR' },
  //...(Platform.OS === 'web' ? [{ key: 'Test', title: 'Test' }] : []),
];

/**
 * The ChargerSelection component is the top-level component for the
 * "Select Charger" tab in the Meander app. It displays a tabbed interface
 * with three tabs: Map, Search, and Scan QR.
 *
 * The Map tab displays a Google Map with chargers pinned at their locations, allowing the user to select a charger by clicking on its pin.
 *
 * The Search tab displays a list of chargers with their names, addresses, and distances from the user's location, sorted by distance.
 *
 * The Scan QR tab displays a QR code scanner that allows the user to scan a QR code to find a charger.
 *
 * The component also handles the logic for calculating the distances from
 * the user's location to each charger, and for sorting the chargers by distance.
 */
export const ChargerSelection: React.FC<Props> = ({ navigation, route }) => {
  const dispatch: AppDispatch = useDispatch();
  const chargerList = useSelector(
    (state: RootState) => state.chargers.chargers
  );
  const currentChargerSelectionTab = useSelector(
    (state: RootState) => (state as any).navigation?.currentChargerSelectionTab
  );

  const [index, setIndex] = useState(0);
  const [myLocation, setMyLocation] = useState<Location.LocationObjectCoords>();
  const [chargersWithDistance, setChargersWithDistance] = useState<
    ChargerAttributes[]
  >([]);
  const [isScreenFocused, setIsScreenFocused] = useState(true);

  // Temporary test mode - set to true to test individual components
  const [testMode, setTestMode] = useState(false);

  const { top } = useSafeAreaInsets();
  const swipeEnabledRef = useRef(true);

  // On mount: get location and chargers
  useEffect(() => {
    const initialize = async () => {
      const userLocation = await getUserLocation();
      setMyLocation(userLocation);
    };

    initialize();
    dispatch(getAllChargers());
  }, []);

  // Set initial tab based on route params or stored state
  useEffect(() => {
    let initialTabIndex = 0;
    
    if (route.params?.tabSelect) {
      // Route params take priority
      initialTabIndex = TABS.findIndex(
        (t) => t.key === route.params.tabSelect
      );
    } else if (currentChargerSelectionTab) {
      // Fall back to stored state
      initialTabIndex = TABS.findIndex(
        (t) => t.key === currentChargerSelectionTab
      );
    }
    
    if (initialTabIndex >= 0 && initialTabIndex !== index) {
      setIndex(initialTabIndex);
    }
  }, [route.params?.tabSelect]); // Only depend on route params, not currentChargerSelectionTab

  // Update stored tab selection when index changes (but not during initial setup)
  useEffect(() => {
    const currentTabKey = TABS[index]?.key;
    if (currentTabKey && currentTabKey !== currentChargerSelectionTab) {
      dispatch(navigationActions.setCurrentChargerSelectionTab(currentTabKey));
    }
  }, [index]);

  // Recalculate distances when chargers or location changes
  useEffect(() => {
    if (chargerList?.length === 0 || !myLocation) return;

    const withDistance = chargerList
      .filter((c) => c && c.is_enabled) // Add null/undefined check
      .map((c) => ({
        ...c,
        totalDistance: getMyDistanceFromCharger(myLocation, c),
      }))
      .sort((a, b) => a.totalDistance - b.totalDistance);

    // More efficient comparison - check if arrays have same length and same charger IDs
    setChargersWithDistance((prev) => {
      if (prev.length !== withDistance.length) {
        return withDistance;
      }

      // Check if charger IDs are the same (more efficient than JSON.stringify)
      const prevIds = prev.map((c) => c.uuid).join(',');
      const newIds = withDistance.map((c) => c.uuid).join(',');

      if (prevIds !== newIds) {
        return withDistance;
      }

      // If IDs are the same, check if distances changed
      const prevDistances = prev.map((c) => c.totalDistance).join(',');
      const newDistances = withDistance.map((c) => c.totalDistance).join(',');

      return prevDistances !== newDistances ? withDistance : prev;
    });
  }, [chargerList, myLocation]);

  // Handle map interaction
  const handleMapInteraction = useCallback((isInteracting: boolean) => {
    swipeEnabledRef.current = !isInteracting;
  }, []);

  // Handle screen focus/blur for camera cleanup
  useFocusEffect(
    useCallback(() => {
      setIsScreenFocused(true);
      
      return () => {
        setIsScreenFocused(false);
      };
    }, [])
  );

  // Handle tab change
  const navigationState = useMemo(() => {
    const state = { index, routes: TABS };
    return state;
  }, [index]);

  // Render tab bar
  const renderTabBar = useCallback(
    (props: any) => {
      const { key, ...restProps } = props;
      return (
        <TabBar
          key={key}
          {...restProps}
          indicatorStyle={{ backgroundColor: 'white' }}
          style={{ backgroundColor: '#1A1D21' }}
        />
      );
    },
    []
  );

  // Map route component
  const MapRoute = useCallback(() => {
    const isMapLoading = chargersWithDistance.length === 0;

    return (
      <View style={styles.scene}>
        <ChargerMapWithLoader
          isLoading={isMapLoading}
          loadingText="Loading chargers near you..."
          navigation={navigation}
          myLocation={myLocation}
          chargersWithDistance={chargersWithDistance}
          onMapInteraction={handleMapInteraction}
        />
      </View>
    );
  }, [myLocation, navigation, chargersWithDistance, handleMapInteraction]);

  // Search route component
  const SearchRoute = useCallback(() => {
    const isSearchLoading = !myLocation || chargersWithDistance.length === 0;

    return (
      <View style={styles.scene}>
        <SearchChargerWithLoader
          isLoading={isSearchLoading}
          navigation={navigation}
          myLocation={myLocation}
          chargersWithDistance={chargersWithDistance}
        />
      </View>
    );
  }, [myLocation, navigation, chargersWithDistance]);

  // Scan QR route component
  const ScanQrRoute = useCallback(() => {
    // Only render QRScanner when the ScanQr tab is actually active AND screen is focused
    if (index !== 2 || !isScreenFocused) {
      return (
        <View style={styles.scene}>
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <Text style={{ color: 'white', fontSize: 16 }}>
              QR Scanner will activate when tab is selected
            </Text>
          </View>
        </View>
      );
    }
    
    return (
      <View style={styles.scene}>
        <QRScanner 
          key={`qr-scanner-${index}-${isScreenFocused}`}
          navigation={navigation} 
          myLocation={myLocation} 
        />
      </View>
    );
  }, [navigation, myLocation, index, isScreenFocused]);

  // Test route component (web only)
  // const TestRoute = useCallback(() => {
  //   if (Platform.OS !== 'web' || index !== 3) return null;

  //   return (
  //     <View style={styles.testContainer}>
  //       <Text style={styles.testTitle}>Testing Components</Text>
  //       <View style={styles.testSection}>
  //         <Text style={styles.testSectionTitle}>
  //           Test 1: WithLoading Component
  //         </Text>
  //         <TestLoadingComponent />
  //       </View>
  //       <View style={styles.testSection}>
  //         <Text style={styles.testSectionTitle}>
  //           Test 2: SearchChargerCard Component
  //         </Text>
  //         <TestSearchChargerCard />
  //       </View>
  //       <View style={styles.testSection}>
  //         <Text style={styles.testSectionTitle}>
  //           Test 3: TestSearchChargerWithLoader Component
  //         </Text>
  //         <TestSearchChargerWithLoader />
  //       </View>
  //       <View style={styles.testSection}>
  //         <Text style={styles.testSectionTitle}>
  //           Test 4: TestRealData Component
  //         </Text>
  //         <TestRealData />
  //       </View>
  //     </View>
  //   );
  // }, [index]);

  // Render scene
  const renderScene = useCallback(
    ({ route, jumpTo, position }: any) => {
      
      switch (route.key) {
        case 'Map':
          return <MapRoute />;
        case 'Search':
          return <SearchRoute />;
        case 'ScanQr':
          return <ScanQrRoute />;
        default:
          return <MapRoute />;
      }
    },
    [MapRoute, SearchRoute, ScanQrRoute, index]
  );

  return (
    <SafeAreaView style={[{ flex: 1, backgroundColor: '#1A1D21' }]}>
      <View
        style={{
          flex: 1,
          backgroundColor: '#1A1D21',
          top: Platform.OS === 'android' ? top : 0,
        }}
      >
        <TabView
          key={`tabview-${index}`}
          navigationState={navigationState}
          renderScene={renderScene}
          onIndexChange={(newIndex) => {
            setIndex(newIndex);
          }}
          initialLayout={{ width: Dimensions.get('window').width }}
          renderTabBar={renderTabBar}
          swipeEnabled={Platform.OS !== 'web'}
          style={{ flex: 1 }}
        />
      </View>
    </SafeAreaView>
  );
};

export default ChargerSelection;

const styles = StyleSheet.create({
  containerBgColor: {
    backgroundColor: '#1A1D21',
  },
  scene: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  tabButton: {
    paddingVertical: 10,
    paddingHorizontal: 25,
    borderRadius: 20,
  },
  tabButtonFocused: {
    backgroundColor: '#39BEB7',
  },
  testToggle: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 1000,
  },
  testButton: {
    padding: 12,
    backgroundColor: '#39BEB7',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: 'white',
  },
  testButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  testContainer: {
    flex: 1,
    backgroundColor: '#1A1D21',
    padding: 20,
  },
  testTitle: {
    color: 'white',
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 20,
  },
  testSection: {
    marginBottom: 20,
  },
  testSectionTitle: {
    color: 'white',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 10,
  },
});
